import type { LendingMode, LenderYields } from '../types'
import { keysFromMaps, rewardsToMap } from '../utils'

export function getRewardYieldOpen(
  amountInUSD: number,
  amountOutUSD: number,
  irModeIn: LendingMode,
  cashFlowDeposits: number,
  cashFlowDebt: number,
  deposits: number,
  debt: number,
  yieldIn: LenderYields,
  yieldOut: LenderYields
) {
  const mapIn = rewardsToMap(yieldIn?.rewards)
  const mapOut = rewardsToMap(yieldOut?.rewards)
  if (!yieldIn?.rewards?.length && !yieldOut?.rewards?.length) return {}

  const data: Record<string, { borrowApr: number; depositApr: number; apr: number }> = {}
  for (const key of keysFromMaps(mapIn, mapOut)) {
    const _flowIn =
      amountInUSD *
      ((irModeIn === 1
        ? mapIn[key]?.stableBorrowRate
        : mapIn[key]?.variableBorrowRate) ?? 0)
    const _flowOut = amountOutUSD * (mapOut[key]?.depositRate ?? 0)
    const totalCashFlowDebt = cashFlowDebt + _flowIn
    const totalCashFlowDeposits = cashFlowDeposits + _flowOut
    const newCollat = deposits + amountOutUSD
    const newDebt = debt + amountInUSD
    data[key] = {
      borrowApr: totalCashFlowDebt / newDebt,
      depositApr: totalCashFlowDeposits / newCollat,
      apr:
        (cashFlowDeposits + cashFlowDebt + _flowIn + _flowOut) /
        (newCollat - newDebt),
    }
  }
  return data
}

export function getRewardYieldClose(
  amountInUSD: number,
  amountOutUSD: number,
  irModeOut: LendingMode,
  cashFlowDeposits: number,
  cashFlowDebt: number,
  deposits: number,
  debt: number,
  yieldIn: LenderYields,
  yieldOut: LenderYields
) {
  const mapIn = rewardsToMap(yieldIn?.rewards)
  const mapOut = rewardsToMap(yieldOut?.rewards)
  if (!yieldIn?.rewards?.length && !yieldOut?.rewards?.length) return {}

  const data: Record<string, { borrowApr: number; depositApr: number; apr: number }> = {}
  for (const key of keysFromMaps(mapIn, mapOut)) {
    const _flowIn = amountInUSD * (mapIn[key]?.depositRate ?? 0)
    const _flowOut =
      amountOutUSD *
      ((irModeOut === 1
        ? mapOut[key]?.stableBorrowRate
        : mapOut[key]?.variableBorrowRate) ?? 0)
    const totalCashFlowDebt = cashFlowDebt - _flowOut
    const totalCashFlowDeposits = cashFlowDeposits - _flowIn
    const newCollat = deposits - amountInUSD
    const newDebt = debt - amountOutUSD
    data[key] = {
      borrowApr: Math.abs(newDebt) < 0.01 ? 0 : totalCashFlowDebt / newDebt,
      depositApr: totalCashFlowDeposits / newCollat,
      apr:
        (cashFlowDeposits + cashFlowDebt - _flowIn - _flowOut) /
        (newCollat - newDebt),
    }
  }
  return data
}

export function getRewardYieldDebtSwap(
  amountInUSD: number,
  amountOutUSD: number,
  irModeIn: LendingMode,
  irModeOut: LendingMode,
  cashFlowDeposits: number,
  cashFlowDebt: number,
  deposits: number,
  debt: number,
  yieldIn: LenderYields,
  yieldOut: LenderYields
) {
  const mapIn = rewardsToMap(yieldIn?.rewards)
  const mapOut = rewardsToMap(yieldOut?.rewards)
  if (!yieldIn?.rewards?.length && !yieldOut?.rewards?.length) return {}

  const data: Record<string, { borrowApr: number; depositApr: number; apr: number }> = {}
  for (const key of keysFromMaps(mapIn, mapOut)) {
    const _flowIn =
      amountInUSD *
      ((irModeIn === 1
        ? mapIn[key]?.stableBorrowRate
        : mapIn[key]?.variableBorrowRate) ?? 0)
    const _flowOut =
      amountOutUSD *
      ((irModeOut === 1
        ? mapOut[key]?.stableBorrowRate
        : mapOut[key]?.variableBorrowRate) ?? 0)
    const totalCashFlowDebt = cashFlowDebt + _flowIn - _flowOut
    const newDebt = debt - amountOutUSD + amountInUSD
    data[key] = {
      borrowApr: Math.abs(newDebt) < 0.01 ? 0 : totalCashFlowDebt / newDebt,
      depositApr: cashFlowDeposits / deposits,
      apr: (cashFlowDeposits + totalCashFlowDebt) / (deposits - newDebt),
    }
  }
  return data
}

export function getRewardYieldCollateralSwap(
  amountInUSD: number,
  amountOutUSD: number,
  cashFlowDeposits: number,
  cashFlowDebt: number,
  deposits: number,
  debt: number,
  yieldIn: LenderYields,
  yieldOut: LenderYields
) {
  const mapIn = rewardsToMap(yieldIn?.rewards)
  const mapOut = rewardsToMap(yieldOut?.rewards)
  if (!yieldIn?.rewards?.length && !yieldOut?.rewards?.length) return {}

  const data: Record<string, { borrowApr: number; depositApr: number; apr: number }> = {}
  for (const key of keysFromMaps(mapIn, mapOut)) {
    const _flowIn = amountInUSD * (mapIn[key]?.depositRate ?? 0)
    const _flowOut = amountOutUSD * (mapOut[key]?.depositRate ?? 0)
    const totalCashFlowDeposits = cashFlowDeposits + _flowOut - _flowIn
    const newCollat = deposits + amountOutUSD - amountInUSD
    data[key] = {
      borrowApr: Math.abs(debt) < 0.01 ? 0 : cashFlowDebt / debt,
      depositApr: totalCashFlowDeposits / newCollat,
      apr: (totalCashFlowDeposits + cashFlowDebt) / (newCollat - debt),
    }
  }
  return data
}
